import { z } from "zod";

/**
 * Derived, **display-only** campaign status (PREC-3249). The API computes it per campaign from
 * config validity + lead state + the persisted per-campaign run status; the web app maps each
 * value to an icon, color and tooltip. It is NOT persisted — see `CampaignStatus` (api/web) for
 * the stored per-campaign run state (DRAFT/RUNNING/PAUSED).
 *
 * - INVALID: no senders or no sequence content; cannot accept leads.
 * - VALID:   configured correctly but no leads assigned yet, and not started. An empty campaign
 *            that was started reads ACTIVE, a paused one PAUSED (PREC-3296).
 * - READY:   leads assigned but the campaign was never activated (no run row yet) — configured and
 *            ready to start, but NOT sending. The user must activate it to begin outreach.
 * - ACTIVE:  running and sending messages.
 * - PAUSED:  temporarily stopped.
 * - ENDED:   started and all sequences completed (no pending schedules left).
 * - LOADING: activation in progress — schedules are being rescheduled before sending starts
 *            (mirrors the list-level LOADING phase); the campaign goes RUNNING once it finishes.
 */
export enum CampaignDisplayStatus {
	INVALID = "INVALID",
	VALID = "VALID",
	READY = "READY",
	ACTIVE = "ACTIVE",
	PAUSED = "PAUSED",
	ENDED = "ENDED",
	LOADING = "LOADING",
}

export const campaignDisplayStatusSchema = z.nativeEnum(CampaignDisplayStatus);

/**
 * Per-campaign status payload returned by the API (keyed by campaign uuid) and consumed by the web
 * app. `locked` is true once a step of the sequence has actually been **processed** for some lead —
 * i.e. something was really sent (PREC-3853). Assigning leads does not lock anything, and a
 * schedule that only ever failed contacted nobody, so it does not lock either. Once set the lock
 * never lifts: removing the contacted leads leaves it in place, because the sequence was already
 * used to send. While locked, structural edits are restricted (steps, conditions, senders and a
 * step's channel); message text stays editable.
 */
export const campaignStatusInfoSchema = z.object({
	status: campaignDisplayStatusSchema,
	locked: z.boolean(),
	/**
	 * When this campaign stopped being able to run, and what the server could see of why
	 * (PREC-4153). Both null while it is fine. An INVALID badge with no date reads as if something
	 * has just broken, which for a campaign nobody has opened in a fortnight is misleading — the
	 * date is exact even when the reasons are empty, because only the builder can compute some of
	 * them.
	 */
	invalidatedAt: z.string().nullish(),
	invalidReasons: z.array(z.string()).nullish(),
});

export type CampaignStatusInfo = z.infer<typeof campaignStatusInfoSchema>;
